import { Link } from 'react-router-dom';
import { Building2, Calculator, ExternalLink, Smartphone, ArrowRight, Sparkles, TrendingUp, Users, Shield, Target, Scale, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

const Tools = () => {
  const tools = [
    {
      id: 'gestion-copropriete',
      title: "Gestion de Copropriété",
      subtitle: "Application gratuite",
      description: "Créez et gérez votre copropriété facilement, même en tant que bénévole. Idéal pour les petites copropriétés sans syndic professionnel.",
      icon: Building2,
      features: [
        { icon: Users, text: "Création de copropriété simplifiée" },
        { icon: Calculator, text: "Gestion des charges et appels de fonds" },
        { icon: Shield, text: "Suivi des travaux et décisions d'AG" },
        { icon: Sparkles, text: "Interface intuitive pour bénévoles" }
      ],
      link: "https://copropriete.app/",
      internalLink: "/nos-outils/gestion-copropriete",
      hasPlayStore: true,
      gradient: "from-blue-600 via-blue-700 to-indigo-800",
      accentColor: "blue"
    },
    {
      id: 'calculateur-rentabilite',
      title: "Calculateur de Rentabilité",
      subtitle: "Outil gratuit en ligne",
      description: "Calculez la rentabilité de votre investissement immobilier en quelques clics. Rendement brut, net, cash-flow... tout y est !",
      icon: Calculator,
      features: [
        { icon: TrendingUp, text: "Calcul du rendement brut et net" },
        { icon: Calculator, text: "Estimation du cash-flow mensuel" },
        { icon: Sparkles, text: "Simulation de crédit intégrée" },
        { icon: Shield, text: "Comparaison de scénarios" }
      ],
      link: null,
      internalLink: "/nos-outils/calculateur-rentabilite",
      hasPlayStore: false,
      gradient: "from-emerald-500 via-emerald-600 to-teal-700",
      accentColor: "emerald"
    },
    {
      id: 'diagnostic-faisabilite-division',
      title: "Diagnostic Faisabilité Division",
      subtitle: "Résultat en 3 minutes",
      description: "Puis-je diviser mon bien ? Répondez à 8 questions et obtenez un score de faisabilité 0-100, un verdict argumenté et une estimation du gain potentiel.",
      icon: Target,
      features: [
        { icon: Scale, text: "Score de faisabilité 0-100" },
        { icon: TrendingUp, text: "Estimation du gain (+5 à +45%)" },
        { icon: MapPin, text: "Analyse PLU & contraintes locales" },
        { icon: Shield, text: "Rappel personnalisé sous 48h" }
      ],
      link: null,
      internalLink: "/nos-outils/diagnostic-faisabilite-division",
      hasPlayStore: false,
      gradient: "from-amber-500 via-orange-600 to-amber-700",
      accentColor: "amber"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Nos Outils Gratuits | G Division - Division Immobilière</title>
        <meta name="description" content="Découvrez nos outils gratuits : application de gestion de copropriété pour bénévoles, calculateur de rentabilité immobilière et diagnostic de faisabilité de division. Simplifiez vos projets immobiliers." />
        <meta name="keywords" content="outil copropriété gratuit, calculateur rentabilité immobilière, diagnostic faisabilité division, puis-je diviser mon bien, gestion copropriété bénévole, simulation investissement immobilier" />
        <link rel="canonical" href="https://divisionimmobiliere.com/nos-outils" />
      </Helmet>

      <div className="pt-20" data-testid="tools-page">
        {/* Hero */}
        <section className="relative bg-[#1e3a5f] py-20 md:py-28 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#c9a227]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-[#c9a227]/20 text-[#c9a227] text-xs font-semibold px-4 py-2 rounded-full mb-6">
                <Sparkles size={14} />
                100% GRATUIT
              </span>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
                Nos Outils
              </h1>
              <p className="text-gray-300 text-lg md:text-xl leading-relaxed">
                Des outils pratiques et gratuits pour vous accompagner dans vos projets 
                de division immobilière, gestion de copropriété et investissement.
              </p>
            </div>
          </div>
        </section>

        {/* Tools Grid - Modern Cards */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
              {tools.map((tool) => (
                <div 
                  key={tool.id}
                  className="group"
                  data-testid={`tool-card-${tool.id}`}
                >
                  {/* Card */}
                  <div className="relative h-full bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100">
                    {/* Colored Header */}
                    <div className={`relative bg-gradient-to-br ${tool.gradient} p-8 pb-20`}>
                      {/* Decorative shapes */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                      
                      <div className="relative z-10">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                            <tool.icon className="text-white" size={28} />
                          </div>
                          {tool.hasPlayStore && (
                            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5">
                              <Smartphone size={14} className="text-white" />
                              <span className="text-white text-xs font-medium">Play Store</span>
                            </div>
                          )}
                        </div>
                        
                        <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-2">
                          {tool.title}
                        </h2>
                        <p className="text-white/80 text-sm font-medium">{tool.subtitle}</p>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="relative px-8 pb-8 -mt-12">
                      {/* Description card */}
                      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-50">
                        <p className="text-gray-600 leading-relaxed">
                          {tool.description}
                        </p>
                      </div>
                      
                      {/* Features */}
                      <div className="space-y-3 mb-8">
                        {tool.features.map((feature, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center gap-3 text-gray-700 group-hover:translate-x-1 transition-transform duration-300"
                            style={{ transitionDelay: `${idx * 50}ms` }}
                          >
                            <div className={`w-8 h-8 rounded-lg bg-${tool.accentColor}-100 flex items-center justify-center flex-shrink-0`}>
                              <feature.icon className={`text-${tool.accentColor}-600`} size={16} />
                            </div>
                            <span className="text-sm font-medium">{feature.text}</span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link to={tool.internalLink} className="flex-1">
                          <Button 
                            className={`w-full bg-gradient-to-r ${tool.gradient} text-white hover:opacity-90 rounded-xl py-6 font-semibold transition-all duration-300 group-hover:shadow-lg`}
                          >
                            Découvrir
                            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                          </Button>
                        </Link>
                        {tool.link && (
                          <a href={tool.link} target="_blank" rel="noopener noreferrer" className="flex-1">
                            <Button 
                              variant="outline"
                              className="w-full border-2 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl py-6 font-semibold"
                            >
                              <ExternalLink size={16} className="mr-2" />
                              Accéder
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 bg-[#1e3a5f] relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#c9a227]/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
            <span className="inline-flex items-center gap-2 bg-white/10 text-[#c9a227] text-xs font-semibold px-4 py-2 rounded-full mb-6">
              <Users size={14} />
              ACCOMPAGNEMENT EXPERT
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Besoin d'un Accompagnement Personnalisé ?
            </h2>
            <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
              Nos outils sont un premier pas. Pour un accompagnement complet de votre projet 
              de division ou d'indivision, contactez nos experts.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-[#c9a227] text-white hover:bg-[#b08d22] rounded-xl px-10 py-6 uppercase tracking-wider text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Consultation Gratuite
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Tools;
